import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { vapi } from '../context/TMSContext';
import { smallBtn } from '../lib/styles';
import { FleetMapPanel } from './FleetGPS';
import LoadMatcher from '../components/LoadMatcher';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { calcProfit } from '../lib/calcProfit';

interface CommandCenterProps {
  companyId: string | null;
  isJakeActive: boolean;
  jakeAlerts: any[];
  drivers: any[];
  data: any[];
  toggleJake: () => void;
  acknowledgeAlert: (id: string) => void;
  setRoom: (room: string) => void;
  setSelectedLoad: (v: any) => void;
}

export default function CommandCenter({ companyId, isJakeActive, jakeAlerts, drivers, data, toggleJake, acknowledgeAlert, setRoom, setSelectedLoad }: CommandCenterProps) {
  const [dash, setDash] = useState<any>(null);
  const [dashLoading, setDashLoading] = useState(false);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [recentLoads, setRecentLoads] = useState<any[]>([]);
  const [huntResults, setHuntResults] = useState<any[]>([]);
  const [huntDrilldown, setHuntDrilldown] = useState<any>(null);
  const [ccIndex, setCcIndex] = useState<{loadsById:Record<string,any>;driversById:Record<string,any>;complianceById:Record<string,any>}>({loadsById:{},driversById:{},complianceById:{}});
  const lastCCRefreshRef = useRef<number>(0);
  const lastCriticalKeysRef = useRef<Set<string>>(new Set());

  const todayYMD = () => new Date().toISOString().slice(0, 10);

  const announceCritical = useCallback((text: string) => {
    try {
      const anyVapi = vapi as any;
      if (isJakeActive && typeof anyVapi?.send === "function") { anyVapi.send({ type: "say", text }); return; }
    } catch {}
    try { if ("speechSynthesis" in window) { const u = new SpeechSynthesisUtterance(text); window.speechSynthesis.speak(u); } } catch {}
  }, [isJakeActive]);

  const fetchCommandCenter = useCallback(async () => {
    if (!companyId) return;
    setDashLoading(true);
    const today = todayYMD();
    const { data: loadsRaw } = await supabase.from("loads").select("id,load_number,status,workflow_status,dispatched_at,pickup_date,pickup_city,pickup_state,delivery_date,delivery_city,delivery_state,profit_margin_percent,is_low_hanging,customer_name,assigned_driver_id,profit_band,rate_total,net_profit,rpm").eq("company_id", companyId).order("created_at", { ascending: false });
    const { data: drvRaw } = await supabase.from("drivers").select("id,full_name,hos_status,status,current_city,current_state").eq("company_id", companyId);
    const { data: compRaw } = await supabase.from("compliance").select("id,document_type,document_name,expiration_date,status,driver_id,related_asset_id").eq("company_id", companyId);
    const loads = loadsRaw ?? []; const drv = drvRaw ?? []; const comp = compRaw ?? [];
    const loadsById: Record<string,any> = {}; const driversById: Record<string,any> = {}; const complianceById: Record<string,any> = {};
    loads.forEach(l => { loadsById[l.id] = l; }); drv.forEach(d => { driversById[d.id] = d; }); comp.forEach(c => { complianceById[c.id] = c; });
    setCcIndex({ loadsById, driversById, complianceById });
    const activeLoads = loads.filter(l => (l.status||"").toUpperCase()==="ACTIVE").length;
    const inTransitLoads = loads.filter(l => (l.status||"").toUpperCase()==="IN TRANSIT").length;
    const deliveredToday = loads.filter(l => (l.status||"").toUpperCase()==="DELIVERED" && (l.delivery_date||"")=== today).length;
    const driversTotal = drv.length; const driversDriving = drv.filter(d=>d.hos_status==="DRIVING").length;
    const now = Date.now(); const in30 = now + 30*86400000;
    const totalRevenue = loads.reduce((s,l)=>s+Number(l.rate_total||0),0);
    const totalNetProfit = loads.reduce((s,l)=>{
      const driver = drv.find((d:any)=>d.id===l.assigned_driver_id);
      return s + calcProfit(l, driver).net_profit;
    }, 0);
    const redLoads = loads.filter(l=>l.profit_band==='RED').length;
    const yellowLoads = loads.filter(l=>l.profit_band==='YELLOW').length;
    const greenLoads = loads.filter(l=>l.profit_band==='GREEN').length;
    const newAlerts: any[] = [];
    loads.forEach(l => {
      const st = (l.status||"").toUpperCase();
      const cust = l.customer_name || "Unknown customer";
      const daysLate = (d: string) => Math.floor((Date.now()-new Date(d).getTime())/86400000);
      const alreadyPickedUp = ['EN_ROUTE_PICKUP','AT_PICKUP','LOADED','EN_ROUTE_DELIVERY','AT_DELIVERY','DELIVERED'].includes(l.workflow_status || '');
      if (l.pickup_date && l.pickup_date < today && ["ACTIVE","PENDING"].includes(st) && !alreadyPickedUp) {
        const d = daysLate(l.pickup_date);
        newAlerts.push({ severity:"CRITICAL", title:`Load ${l.load_number} missed pickup${d>1?` — ${d} days ago`:''}`, detail:`${cust} was due ${l.pickup_date} in ${l.pickup_city||'?'}, ${l.pickup_state||'?'}. Not yet picked up.`, ref:{table:"loads",id:l.id} });
      }
      if (l.delivery_date && l.delivery_date < today && ["ACTIVE","IN TRANSIT","PENDING"].includes(st)) {
        const d = daysLate(l.delivery_date);
        newAlerts.push({ severity:"CRITICAL", title:`Load ${l.load_number} is${d>1?` ${d} days`:''}  late to ${l.delivery_city||'delivery'}`, detail:`${cust} — due ${l.delivery_date}. ${l.assigned_driver_id?'Check on the driver.':'No driver assigned — this load is sitting.'}`, ref:{table:"loads",id:l.id} });
      }
      if (["ACTIVE","PENDING"].includes(st) && !l.assigned_driver_id) newAlerts.push({ severity:"WARN", title:`Load ${l.load_number} needs a driver`, detail:`${cust} — ${st.toLowerCase()} with no driver assigned. Pickup is ${l.pickup_date||'unscheduled'}.`, ref:{table:"loads",id:l.id} });
      if (["ACTIVE","IN TRANSIT","PENDING"].includes(st)) {
        const margin = Number(l.profit_margin_percent??0);
        if (isFinite(margin) && margin < 5) newAlerts.push({ severity:"CRITICAL", title:`Load ${l.load_number} is losing money`, detail:`${cust} — margin is only ${margin.toFixed(1)}%. Rate or cost needs to be reviewed now.`, ref:{table:"loads",id:l.id} });
        else if (isFinite(margin) && margin < 10) newAlerts.push({ severity:"WARN", title:`Thin margin on Load ${l.load_number}`, detail:`${cust} — ${margin.toFixed(1)}% margin. Keep an eye on fuel and driver pay.`, ref:{table:"loads",id:l.id} });
      }
    });
    const hasWork = loads.some(l=>["ACTIVE","IN TRANSIT"].includes((l.status||"").toUpperCase()));
    if (!hasWork) drv.forEach(d => { if (["ON DUTY","DRIVING"].includes(d.hos_status)) newAlerts.push({ severity:"WARN", title:`${d.full_name||"Driver"} is on the clock with no load`, detail:`${d.hos_status} in ${d.current_city||"?"}, ${d.current_state||"?"}. No active loads in the system right now.`, ref:{table:"drivers",id:d.id} }); });
    comp.forEach(c => {
      if (!c.expiration_date) return;
      const t = new Date(c.expiration_date).getTime();
      const daysLeft = Math.floor((t - now) / 86400000);
      if (t < now) newAlerts.push({ severity:"CRITICAL", title:`${c.document_type||"Compliance doc"} is expired`, detail:`${c.document_name||"Document"} expired ${Math.abs(daysLeft)} day${Math.abs(daysLeft)!==1?'s':''} ago. Fix this before the next DOT inspection.`, ref:{table:"compliance",id:c.id} });
      else if (t <= in30) newAlerts.push({ severity:"WARN", title:`${c.document_type||"Compliance doc"} expires in ${daysLeft} day${daysLeft!==1?'s':''}`, detail:`${c.document_name||"Document"} — due ${c.expiration_date}. Schedule renewal now.`, ref:{table:"compliance",id:c.id} });
    });
    const seen = new Set<string>();
    const dedupedAlerts = newAlerts.filter(a => { const key=`${a.ref?.table||""}:${a.ref?.id||""}:${a.title||""}`; if(seen.has(key))return false; seen.add(key); return true; });
    dedupedAlerts.sort((a,b)=>a.severity===b.severity?0:a.severity==="CRITICAL"?-1:1);
    dedupedAlerts.forEach(a => {
      if (a.severity!=="CRITICAL") return;
      const key=`${a.ref?.table||""}:${a.ref?.id||""}:${a.title||""}`;
      if (!lastCriticalKeysRef.current.has(key)) { lastCriticalKeysRef.current.add(key); announceCritical(a.title); }
    });
    setAlerts(dedupedAlerts.slice(0,30));
    setRecentLoads(loads.slice(0, 5));
    setDash({ activeLoads, inTransitLoads, deliveredToday, driversTotal, driversDriving, totalRevenue, totalNetProfit, redLoads, yellowLoads, greenLoads });
    // Fetch hunt results
    const { data: hunts } = await supabase.from('load_board_hunt_results').select('*').eq('company_id', companyId).order('created_at', { ascending:false }).limit(10);
    setHuntResults(hunts || []);
    setDashLoading(false);
  }, [companyId, announceCritical]);

  const handleAlertClick = useCallback(async (a: any) => {
    const table = a?.ref?.table; const id = a?.ref?.id;
    if (!table || !id) return;
    if (table === "loads") {
      setRoom("Control Tower");
      // Always fetch full record — ccIndex only has partial fields
      const { data: row } = await supabase.from("loads").select("*").eq("id", id).maybeSingle();
      if (row) setSelectedLoad(row);
    } else if (table === "drivers") {
      setRoom("Driver Management");
      const { data: row } = await supabase.from("drivers").select("*").eq("id", id).maybeSingle();
      if (row) setSelectedLoad(row);
    } else if (table === "compliance") {
      setRoom("Compliance Room"); setSelectedLoad(null);
    }
  }, [setRoom, setSelectedLoad]);

  // Fetch on mount + auto-refresh every 60s
  useEffect(() => {
    if (!companyId) return;
    fetchCommandCenter();
    const interval = setInterval(fetchCommandCenter, 60000);
    return () => clearInterval(interval);
  }, [companyId, fetchCommandCenter]);

  // Realtime subscription — auto-refresh when loads/drivers change
  useEffect(() => {
    if (!companyId) return;
    const ch = supabase.channel(`cc-live-${companyId}`)
      .on("postgres_changes", { event:"*", schema:"public", table:"loads", filter:`company_id=eq.${companyId}` }, () => {
        const now = Date.now();
        if (now - lastCCRefreshRef.current > 800) { lastCCRefreshRef.current = now; fetchCommandCenter(); }
      })
      .on("postgres_changes", { event:"*", schema:"public", table:"drivers", filter:`company_id=eq.${companyId}` }, () => {
        const now = Date.now();
        if (now - lastCCRefreshRef.current > 800) { lastCCRefreshRef.current = now; fetchCommandCenter(); }
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [companyId, fetchCommandCenter]);

  const handleLoadClick = async (load: any) => {
    setRoom("Control Tower");
    // Always fetch full record so Control Tower sees all fields
    const { data: row } = await supabase.from("loads").select("*").eq("id", load.id).maybeSingle();
    if (row) setSelectedLoad(row);
  };

  const statusColor = (s: string) => {
    if (s === 'IN TRANSIT') return '#4488FF';
    if (s === 'ACTIVE') return '#FFD700';
    if (s === 'DELIVERED') return '#32CD32';
    if (s === 'CANCELLED') return '#777';
    return '#aaa';
  };

  const unackedAlerts = jakeAlerts.filter(a => !a.acknowledged);

  return (
    <div>
      <OnboardingChecklist companyId={companyId} setRoom={setRoom} />
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px" }}>
        <div style={{ color:"#aaa", fontSize:"11px" }}>Jake: {isJakeActive ? <span style={{color:"#32CD32"}}>CONNECTED</span> : "STANDBY"}</div>
        <div style={{ display:"flex", gap:"10px" }}>
          <button onClick={toggleJake} style={isJakeActive ? {...smallBtn,background:"#32CD32"} : {...smallBtn,background:"transparent",color:"#32CD32",border:"1px solid #32CD32"}}>{isJakeActive ? "DISCONNECT JAKE" : "ACTIVATE JAKE"}</button>
          <button onClick={fetchCommandCenter} style={smallBtn}>↻ REFRESH</button>
        </div>
      </div>

      {/* KPI GRID */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:"10px", marginBottom:"20px" }}>
        {[
          {label:"ACTIVE LOADS",value:dash?.activeLoads??0,color:"#FFD700"},
          {label:"IN TRANSIT",value:dash?.inTransitLoads??0,color:"#4488FF"},
          {label:"DELIVERED TODAY",value:dash?.deliveredToday??0,color:"#32CD32"},
          {label:"TOTAL REVENUE",value:`$${Number(dash?.totalRevenue??0).toLocaleString()}`,color:"#32CD32"},
          {label:"EST. NET PROFIT",value:`$${Number(dash?.totalNetProfit??0).toLocaleString()}`,color:Number(dash?.totalNetProfit??0)>=0?"#32CD32":"#ff4444"},
          {label:"DRIVERS TOTAL",value:dash?.driversTotal??0,color:"#ccc"},
          {label:"DRIVING",value:dash?.driversDriving??0,color:"#4488FF"},
          {label:"GREEN LOADS",value:dash?.greenLoads??0,color:"#32CD32"},
          {label:"YELLOW LOADS",value:dash?.yellowLoads??0,color:"#FFD700"},
          {label:"RED LOADS",value:dash?.redLoads??0,color:"#ff4444"},
        ].map((k,i)=>(
          <div key={i} style={{ border:"1px solid #1a1a1a", background:"#050505", padding:"14px" }}>
            <div style={{ color:"#777", fontSize:"9px", letterSpacing:"1.5px", marginBottom:"6px" }}>{k.label}</div>
            <div style={{ color:k.color, fontSize:"20px", fontWeight:900 }}>{dashLoading?"…":k.value}</div>
          </div>
        ))}
      </div>

      {/* JAKE ALERTS + SYSTEM ALERTS */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px" }}>
        <div style={{ background:"#0a0a0a", border:"1px solid #1e1e1e", padding:"20px" }}>
          <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", marginBottom:"12px", display:"flex", justifyContent:"space-between" }}>
            <span>JAKE INTELLIGENCE ALERTS</span>
            {unackedAlerts.length > 0 && <span style={{ background:"#ff4444", color:"#fff", fontSize:"9px", padding:"2px 6px" }}>{unackedAlerts.length}</span>}
          </div>
          {unackedAlerts.length === 0 ? (
            <div style={{ color:"#555", fontSize:"11px" }}>✓ All clear — Jake is monitoring</div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:"8px", maxHeight:"320px", overflowY:"auto" }}>
              {unackedAlerts.map(a=>(
                <div key={a.id} style={{ borderLeft:`3px solid ${a.level==='RED'?'#ff4444':a.level==='YELLOW'?'#FFD700':'#32CD32'}`, background:a.level==='RED'?'rgba(255,68,68,0.08)':'rgba(255,215,0,0.08)', padding:"10px 12px", flexShrink:0 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ flex:1 }}>
                      <span style={{ fontSize:"9px", fontWeight:700, color:a.level==='RED'?'#ff4444':a.level==='YELLOW'?'#FFD700':'#32CD32', border:"1px solid currentColor", padding:"1px 5px", marginRight:"6px" }}>{a.level}</span>
                      {a.load_number && <span style={{ fontSize:"10px", color:"#32CD32", marginRight:"6px" }}>#{a.load_number}</span>}
                      {a.driver_name && <span style={{ fontSize:"10px", color:"#aaa" }}>{a.driver_name}</span>}
                      <p style={{ margin:"4px 0 0", fontSize:"11px", color:"#ccc", lineHeight:"1.4" }}>{a.message}</p>
                      <p style={{ margin:"2px 0 0", fontSize:"9px", color:"#555" }}>{a.timestamp.toLocaleTimeString()}</p>
                    </div>
                    <button onClick={()=>acknowledgeAlert(a.id)} style={{ background:"transparent", border:"none", color:"#555", cursor:"pointer", fontSize:"16px", padding:"0 4px", flexShrink:0 }}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ background:"#0a0a0a", border:"1px solid #1e1e1e", padding:"20px" }}>
          <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", marginBottom:"12px" }}>SYSTEM ALERTS (LIVE)</div>
          {dashLoading ? <div style={{ color:"#aaa", fontSize:"12px" }}>Loading…</div> : alerts.length === 0 ? <div style={{ color:"#555", fontSize:"12px" }}>No active alerts.</div> : (
            <div style={{ display:"flex", flexDirection:"column", gap:"8px", maxHeight:"320px", overflowY:"auto" }}>
              {alerts.map((a,i)=>(
                <div key={i} onClick={()=>handleAlertClick(a)} style={{ border:"1px solid #1a1a1a", background:"#050505", padding:"10px", cursor:a?.ref?.table&&a?.ref?.id?"pointer":"default", flexShrink:0 }}>
                  <div style={{ display:"flex", justifyContent:"space-between" }}>
                    <div style={{ fontWeight:800, color:a.severity==="CRITICAL"?"#ff4444":"#ffaa00", fontSize:"10px" }}>{a.severity==="CRITICAL"?"CRITICAL":"WARNING"}</div>
                    <div style={{ color:"#777", fontSize:"10px" }}>{a.ref?.table||""}</div>
                  </div>
                  <div style={{ marginTop:"4px", color:"#fff", fontWeight:700, fontSize:"12px" }}>{a.title}</div>
                  <div style={{ marginTop:"4px", color:"#aaa", fontSize:"11px" }}>{a.detail}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RECENT LOADS */}
      <div style={{ marginTop:"20px", border:"1px solid #1e1e1e", background:"#0a0a0a" }}>
        <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, padding:"12px 16px", borderBottom:"1px solid #1e1e1e" }}>RECENT LOADS</div>
        {recentLoads.length === 0 ? (
          <div style={{ padding:"16px", color:"#555", fontSize:"12px" }}>No loads found.</div>
        ) : (
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr>
                {["LOAD #","CUSTOMER","ROUTE","STATUS","RPM","NET PROFIT"].map(h=>(
                  <th key={h} style={{ textAlign:"left", color:"#32CD32", fontSize:"10px", letterSpacing:"1px", borderBottom:"1px solid #1e1e1e", padding:"8px 12px", background:"#050505", fontWeight:700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentLoads.map((l,i)=>(
                <tr key={i} onClick={()=>handleLoadClick(l)} style={{ borderBottom:"1px solid #111", cursor:"pointer" }}
                  onMouseEnter={e=>(e.currentTarget as HTMLElement).style.background="#111"}
                  onMouseLeave={e=>(e.currentTarget as HTMLElement).style.background="transparent"}>
                  <td style={{ padding:"10px 12px", color:"#32CD32", fontWeight:700, fontSize:"12px" }}>#{l.load_number||"---"}</td>
                  <td style={{ padding:"10px 12px", fontSize:"12px" }}>{l.customer_name||"---"}</td>
                  <td style={{ padding:"10px 12px", fontSize:"11px", color:"#aaa" }}>{l.pickup_state||"?"} → {l.delivery_state||"?"}</td>
                  <td style={{ padding:"10px 12px" }}>
                    <span style={{ fontSize:"10px", fontWeight:700, color:statusColor(l.status||""), border:`1px solid ${statusColor(l.status||"")}`, padding:"2px 6px" }}>{l.status||"---"}</span>
                  </td>
                  <td style={{ padding:"10px 12px", fontSize:"12px", color:"#ccc" }}>{l.rpm?`$${Number(l.rpm).toFixed(2)}`:"---"}</td>
                  <td style={{ padding:"10px 12px", fontSize:"12px", color:Number(l.net_profit||0)>=0?"#32CD32":"#ff4444", fontWeight:700 }}>{l.net_profit!=null?`$${Number(l.net_profit).toFixed(0)}`:"---"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* LOAD BOARD HUNT RESULTS */}
      {huntResults.length > 0 && (
        <div style={{ marginTop:"20px", border:"1px solid #1e1e1e", background:"#0a0a0a" }}>
          <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, padding:"12px 16px", borderBottom:"1px solid #1e1e1e", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span>LOAD BOARD HUNT RESULTS</span>
            {huntDrilldown && <button onClick={()=>setHuntDrilldown(null)} style={{ color:"#32CD32", background:"none", border:"none", cursor:"pointer", fontSize:"11px" }}>← BACK TO HUNT LIST</button>}
          </div>
          {huntDrilldown ? (
            <div style={{ padding:"16px" }}>
              <div style={{ color:"#aaa", fontSize:"12px", marginBottom:"12px" }}>
                {huntDrilldown.triggered_by === 'auto_backhaul' ? '🔄 AUTO-BACKHAUL' : '🎙 JAKE VOICE'} — {huntDrilldown.search_origin}{huntDrilldown.search_destination ? ` → ${huntDrilldown.search_destination}` : ' (any destination)'} on {huntDrilldown.search_date} — {huntDrilldown.result_count} result{huntDrilldown.result_count!==1?'s':''}
              </div>
              {(!huntDrilldown.results || huntDrilldown.results.length === 0) ? (
                <div style={{ color:"#555", fontSize:"12px" }}>No loads found in this hunt.</div>
              ) : (
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"11px" }}>
                  <thead><tr>
                    {['BOARD','ORIGIN → DEST','MILES','RATE','RPM','EQUIPMENT','BROKER','ACTION'].map(h=>(
                      <th key={h} style={{ padding:"6px 10px", background:"#050505", color:"#555", textAlign:"left", borderBottom:"1px solid #1a1a1a", fontSize:"10px", letterSpacing:"1px" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {huntDrilldown.results.map((r:any, i:number)=>(
                      <tr key={i} style={{ borderBottom:"1px solid #111" }}>
                        <td style={{ padding:"8px 10px", color:"#32CD32", fontWeight:700 }}>{r.board||'—'}</td>
                        <td style={{ padding:"8px 10px", color:"#aaa" }}>{r.origin||'—'} → {r.destination||'any'}</td>
                        <td style={{ padding:"8px 10px" }}>{r.miles||'—'}</td>
                        <td style={{ padding:"8px 10px", color:"#32CD32", fontWeight:700 }}>{r.rate?`$${r.rate}`:'On call'}</td>
                        <td style={{ padding:"8px 10px" }}>{r.rpm?`$${Number(r.rpm).toFixed(2)}`:'—'}</td>
                        <td style={{ padding:"8px 10px", color:"#aaa" }}>{r.equipment_type||'—'}</td>
                        <td style={{ padding:"8px 10px", color:"#aaa", fontSize:"10px" }}>{r.broker_name||'—'}{r.broker_phone?`\n${r.broker_phone}`:''}</td>
                        <td style={{ padding:"8px 10px" }}>
                          <button onClick={()=>{
                            setRoom('Control Tower');
                            // Pre-fill new load form with hunt data — signal via sessionStorage
                            sessionStorage.setItem('ct_prefill', JSON.stringify({ pickup_city:(r.origin||'').split(',')[0]?.trim(), pickup_state:(r.origin||'').split(',')[1]?.trim(), delivery_state:r.destination?.trim(), rate_total:r.rate||0, loaded_miles:r.miles||0, equipment_type:r.equipment_type||'', customer_name:r.broker_name||'' }));
                          }} style={smallBtn}>BOOK</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12px" }}>
              <thead><tr>
                {['DATE','TRIGGERED BY','ORIGIN','BOARDS','HITS','STATUS','ACTION'].map(h=>(
                  <th key={h} style={{ padding:"8px 12px", background:"#050505", color:"#555", textAlign:"left", borderBottom:"1px solid #1a1a1a", fontSize:"10px", letterSpacing:"1px" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {huntResults.map((h:any, i:number)=>(
                  <tr key={i} style={{ borderBottom:"1px solid #111" }}>
                    <td style={{ padding:"10px 12px", color:"#aaa" }}>{h.search_date}</td>
                    <td style={{ padding:"10px 12px", color:"#32CD32", fontSize:"10px" }}>{h.triggered_by==='auto_backhaul'?'🔄 AUTO-BACKHAUL':h.triggered_by==='jake_voice'?'🎙 JAKE VOICE':'MANUAL'}</td>
                    <td style={{ padding:"10px 12px" }}>{h.search_origin}{h.search_destination?` → ${h.search_destination}`:''}</td>
                    <td style={{ padding:"10px 12px", color:"#aaa", fontSize:"10px" }}>{h.board_name}</td>
                    <td style={{ padding:"10px 12px", color:h.result_count>0?'#32CD32':'#555', fontWeight:700 }}>{h.result_count}</td>
                    <td style={{ padding:"10px 12px" }}><span style={{ color:h.status==='COMPLETE'?'#32CD32':h.status==='FAILED'?'#ff4444':'#FFD700', fontSize:"10px" }}>{h.status}</span></td>
                    <td style={{ padding:"10px 12px" }}><button onClick={()=>setHuntDrilldown(h)} style={smallBtn}>VIEW</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* AI LOAD MATCHER */}
      <div style={{ marginTop:"20px" }}>
        <LoadMatcher companyId={companyId} />
      </div>

      {/* LIVE FLEET MAP */}
      <div style={{ marginTop:"20px", border:"1px solid #1e1e1e", background:"#0a0a0a" }}>
        <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, padding:"12px 16px", borderBottom:"1px solid #1e1e1e", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span>FLEET POSITIONS — LIVE</span>
          <span style={{ color:"#555", fontSize:"9px" }}>{drivers.filter(d=>d.current_city).length} drivers with known location</span>
        </div>
        <FleetMapPanel drivers={drivers} loads={data} apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY||''} />
      </div>
    </div>
  );
}
